import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {pluralize} from 'BenchlingUtil';
import DocumentStore from 'notebook/documents/DocumentStore';
import {ENTRY_TABLE_CELL_COUNT_LIMIT, TableLimitUtils} from 'notebook/performance/tableLimitUtils';
import {TablePerformanceSummaryBar} from 'notebook/performance/TablePerformanceSummaryBar';
import {TablePerformanceSummaryTable} from 'notebook/performance/TablePerformanceSummaryTable';
import {Table} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {AnyStructuredTable, Note} from 'notebook/util/NoteUtilConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Modal from 'ui-library/modals/Modal';

interface NotebookPerformanceMonitorModalProps {
  note: Note;
  onRequestClose: () => void;
}

interface MultiDocumentPerformanceMonitorModalProps {
  documentIds: Array<string>;
  onRequestClose: () => void;
}

export function MultiDocumentPerformanceMonitorModal({
  onRequestClose,
  documentIds,
}: MultiDocumentPerformanceMonitorModalProps): JSX.Element {
  const tablesByDocumentName = useMemo(() => {
    const tablesByDocumentName: {[documentName: string]: Array<Table | AnyStructuredTable>} = {};
    for (const documentId of documentIds) {
      const document = DocumentStore.getDocument(documentId);
      if (document) {
        tablesByDocumentName[document.name] = TableUtil.getTablesFromNote(
          document.documentVersion.entryVersion.note,
          {
            addRunSchemaName: true,
            removeMixtureIngredientSuffix: true,
          }
        );
      }
    }
    return tablesByDocumentName;
  }, [documentIds]);
  return (
    <div>
      <Modal title="Worksheet Table Performance Breakdown" onRequestClose={onRequestClose}>
        <NotebookPerformanceMonitor tablesByDocumentName={tablesByDocumentName} />
      </Modal>
    </div>
  );
}

export function NotebookPerformanceMonitorModal({
  onRequestClose,
  note,
}: NotebookPerformanceMonitorModalProps): JSX.Element {
  const tables = useMemo(
    () =>
      ({
        entry: TableUtil.getTablesFromNote(note, {
          addRunSchemaName: true,
          removeMixtureIngredientSuffix: true,
        }),
      }) satisfies {[documentName: string]: Array<Table | AnyStructuredTable>},
    [note]
  );
  return (
    <div
      onMouseDown={(e): void => {
        e.stopPropagation();
      }}
    >
      <Modal title="Table Performance Breakdown" onRequestClose={onRequestClose}>
        <NotebookPerformanceMonitor tablesByDocumentName={tables} />
      </Modal>
    </div>
  );
}

export function NotebookPerformanceMonitor({
  tablesByDocumentName,
}: {
  tablesByDocumentName: {[documentName: string]: Array<Table | AnyStructuredTable>};
}): JSX.Element {
  const [focusedTableId, setFocusedTableId] = React.useState<string | null>(null);

  const hasMultipleDocuments = Object.keys(tablesByDocumentName).length > 1;

  const {numDocuments, numTables, numCells, tableSummaries} =
    TableLimitUtils.analyzePerformanceOfTables(tablesByDocumentName);

  return (
    <div>
      <TablePerformanceSummaryBar
        limitThreshold={ENTRY_TABLE_CELL_COUNT_LIMIT}
        numCells={numCells}
        tableSummaries={tableSummaries}
        focusedTableId={focusedTableId}
        onHover={(tableId): void => {
          setFocusedTableId(tableId);
        }}
        onLeave={(): void => {
          setFocusedTableId(null);
        }}
      />
      {TableLimitUtils.sizeIsInWarningRange(numCells) && (
        <section className={css(styles.warningSection)}>
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            icon="gauge"
            text="Approaching table performance limit"
            supportingInfo="Adding more tables, rows, and columns may cause slowdowns"
          />
        </section>
      )}
      {TableLimitUtils.sizeIsOverMaximum(numCells) && (
        <section className={css(styles.warningSection)}>
          <InlineBanner
            bannerType={InlineBannerType.ERROR}
            icon="gauge-max"
            text="Table performance limit reached"
            supportingInfo="To resolve this, break up the entry or reduce table size"
          />
        </section>
      )}
      <div className={css(styles.metricsContainer)}>
        {hasMultipleDocuments && (
          <div className={css(styles.metricValue)}>
            {numDocuments.toLocaleString()} {pluralize(numDocuments, 'step', 'steps')}
          </div>
        )}
        <div className={css(styles.metricValue)}>{numCells.toLocaleString()} table cells</div>
        <div className={css(styles.metricValue)}>{numTables.toLocaleString()} tables</div>
      </div>
      <TablePerformanceSummaryTable
        tableSummaries={tableSummaries}
        focusedTableId={focusedTableId}
        showStepNameColumn={hasMultipleDocuments}
        onHover={(tableId): void => {
          setFocusedTableId(tableId);
        }}
        onLeave={(): void => {
          setFocusedTableId(null);
        }}
      />
      <div>
        Check out our&nbsp;
        <a
          className={css(styles.helpLink)}
          href="https://help.benchling.com/hc/en-us/articles/35379317900557-Understanding-and-Managing-Notebook-Entry-Size"
          target="_blank"
          rel="noopener noreferrer"
        >
          <b>Help center article</b> <BenchlingIcon icon="open" />
        </a>
        &nbsp;for tips to improve performance
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  warningSection: {
    marginTop: '16px',
  },
  metricsContainer: {
    display: 'flex',
    flexDirection: 'row',
    padding: '18px 0',
  },
  metricValue: {
    fontSize: '1.5em',
    paddingRight: '24px',
  },
  helpLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
});
